import { User } from '../auth/auth.state'
import {
  Basis,
  IncomingPayoutAgreement,
  PayoutFrequency
} from '../payout-agreement/payout-agreement.state'

// Standard component types for consistency
export enum PayrollComponentType {
  // Earnings
  BASIC = 'BASIC',
  SPECIAL_ALLOWANCE = 'SPECIAL_ALLOWANCE',
  ARREARS = 'ARREARS',
  BONUS = 'BONUS',
  EXPENSE_REIMBURSEMENT = 'EXPENSE_REIMBURSEMENT',
  // Deductions
  LOP_DEDUCTION = 'LOP_DEDUCTION',
  TDS = 'TDS',
  STATUTORY_PF = 'STATUTORY_PF',
  STATUTORY_ESI = 'STATUTORY_ESI',
  STATUTORY_PT = 'STATUTORY_PT',
  ADJUSTMENT = 'ADJUSTMENT' // Can be positive or negative
}

export interface PayrollComponent {
  name: string // e.g., "Basic Salary", "Loss of Pay", "TDS"
  type: PayrollComponentType
  amount: number
  // Optional metadata for more context on the invoice
  meta?: Record<string, any> // e.g., { days: 2, dailyRate: 5000 } for LOP
}

export interface CPCalculationBreakdown {
  // Summary values
  grossForPeriod: number // The gross calculated for the pay period days
  totalEarnings: number // grossForPeriod + bonus + arrears + expenses...
  totalDeductions: number
  netPayable: number

  // Detailed lists
  earnings: PayrollComponent[]
  deductions: PayrollComponent[]

  // Contextual info used for calculation
  payableDays: number
  lopDays: number
  dailyRate: number // The effective daily rate used for LOP deductions
  prorationDetails: ProrationDetails | null
}

export interface ProrationDetails {
  period1: {
    startDate: string
    endDate: string
    days: number
    dailyRate: number
    gross: number
  }
  period2: {
    startDate: string
    endDate: string
    days: number
    dailyRate: number
    gross: number
  }
}

export interface RecalculatePayrollRequest {
  payoutAgreementId: string
  belongsTo: string
  payPeriodStart: string
  payPeriodEnd: string
  inputs: {
    tds: number
    totalHours?: number
    lopDays: number
    bonus: number
    expenses: number
    adjustments: number
    rounding: number
  }
}

export interface ContractorPayroll {
  id: string | null
  employeeId: string
  belongsTo: string
  payoutAgreementId: string
  grossForPeriod: number
  arrears: number
  // Fields for frontend LOP calculation
  prorationDetails: ProrationDetails | null
  dailyRateForPeriod: number

  basis: Basis
  totalHours: number | null
  grossPayout: number
  netPayout: number
  tds: number
  lopDays: number
  bonus: number
  expenses: number
  adjustments: number
  rounding: number
  calculationBreakdown: CPCalculationBreakdown
  status: ContractorPayrollStatus
  batchId: string | null
}

export enum ContractorPayrollStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  IN_PROCESS = 'in-process',
  PROCESSED = 'processed',
  PAID = 'paid',
  FAILED = 'failed'
}

export interface IncomingContractorPayroll extends ContractorPayroll {
  payoutAgreement: IncomingPayoutAgreement
  employee: User
  bankName: string
  bankAccountNumber: string
  pan: string
  businessName: string
  businessAddress: string
  businessLogo: string
  tdsPercent: number
}

export interface BasePayrollBatch {
  id: string | null
  belongsTo: string
  payPeriodStart: string
  payPeriodEnd: string
  payoutFrequency: PayoutFrequency
  netAmount: number | null
  totalTds: number
  totalAmount: number | null
  contractorCount: number | null
  status: ContractorPayrollStatus
  processedBy: string | null
  paidBy: string | null
}

export interface IncomingRunningPayrollBatch extends BasePayrollBatch {
  contractorPayrolls: {
    fixed: IncomingContractorPayroll[] | []
    hourly: IncomingContractorPayroll[] | []
  }
  updatedAt: string | null
  payoutProcessedByUser: User | null
  processedOn: string | null
  payoutPaidByUser: User | null
  dateOfPayment: string | null
}

export interface RunningPayrollBatch extends BasePayrollBatch {
  contractorPayrolls: {
    fixed: ContractorPayroll[] | []
    hourly: ContractorPayroll[] | []
  } | null
}

//Used for manage payroll batches listing
export interface IncomingPayrollBatch extends BasePayrollBatch {
  id: string
  payoutPaidByUser: User | null
  dateOfPayment: string | null
  modeOfPayment: string | null
  payoutProcessedByUser: User | null
  processedOn: string | null
  createdAt: String | null
  updatedAt: string | null
}

export interface PayrollState {
  payrollBatches: IncomingPayrollBatch[] | null
  runningPayroll: IncomingRunningPayrollBatch | null
  contractorPayrollData: IncomingContractorPayroll | null
  loading: boolean
  error: string | null
}
